import React, { useMemo, useState } from "react";
import {
  Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle,
  IconButton, TextField, Tooltip, Typography,
} from "@mui/material";
import VpnKeyRoundedIcon from "@mui/icons-material/VpnKeyRounded";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import BlockIcon from "@mui/icons-material/Block";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import DeleteIcon from "@mui/icons-material/Delete";
import {
  useGetApiClientsQuery, useCreateApiClientMutation,
  useRevokeApiClientMutation, useDeleteApiClientMutation, useRegenerateApiClientMutation,
} from "../../../Api/platformApi";
import {
  AppPage, PageHeader, FilterBar, SearchBox, DataTable, StatusBadge,
  ActionButton, ConfirmDialog, CardSurface, type DataTableColumn,
} from "../../../Components/ui";
import { NAVY } from "../../../theme/tokens";

const GOLD_GLOW = "0 0 0 2px rgba(5,141,208,0.2)";

const btnBase = {
  borderRadius: "8px",
  padding: "6px",
  border: "1px solid #E5E7EB",
  backgroundColor: "#FFFFFF",
  transition: "all 0.2s ease",
  "&:hover": { transform: "translateY(-1px)" },
  "&:active": { transform: "scale(0.96)" },
};

const ApiClients: React.FC = () => {
  const { data, isLoading } = useGetApiClientsQuery();
  const [createClient] = useCreateApiClientMutation();
  const [revokeClient] = useRevokeApiClientMutation();
  const [deleteClient] = useDeleteApiClientMutation();
  const [regenerateClient] = useRegenerateApiClientMutation();

  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [newKey, setNewKey] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [confirmDeleteId, setConfirmDeleteId] = useState<number | null>(null);
  const [confirmRegenId, setConfirmRegenId] = useState<number | null>(null);

  const rows = data?.data ?? [];

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((c: any) =>
      [c.name, c.api_key_prefix]
        .filter(Boolean).some((v: string) => v.toLowerCase().includes(q)));
  }, [rows, search]);

  const save = async () => {
    const res: any = await createClient({ name }).unwrap();
    setNewKey(res.api_key);
    setName("");
  };

  const handleRegenConfirm = async () => {
    if (confirmRegenId === null) return;
    const res: any = await regenerateClient(confirmRegenId).unwrap();
    setConfirmRegenId(null);
    setName("");
    setNewKey(res.api_key);
    setOpen(true);
  };

  const columns: DataTableColumn<any>[] = [
    {
      id: "name", label: "Name",
      render: (c) => (
        <Typography fontSize={14} fontWeight={600} color={NAVY}>{c.name}</Typography>
      ),
    },
    {
      id: "api_key_prefix", label: "Key Prefix",
      render: (c) => (
        <Box component="code" sx={{ fontSize: 13, color: "text.secondary" }}>{c.api_key_prefix}…</Box>
      ),
    },
    { id: "last_used_at", label: "Last Used", render: (c) => c.last_used_at ?? "never" },
    { id: "status", label: "Status", render: (c) => <StatusBadge status={c.status} /> },
  ];

  return (
    <AppPage
      disablePaper
      header={
        <PageHeader
          icon={<VpnKeyRoundedIcon />}
          title="API Clients"
          subtitle="Manage API keys for external integrations"
          actions={
            <ActionButton
              startIcon={<AddRoundedIcon />}
              onClick={() => { setName(""); setNewKey(null); setOpen(true); }}
            >
              Create API Key
            </ActionButton>
          }
        />
      }
    >
      <FilterBar right={<Typography fontSize={13} color="text.secondary">{filtered.length} clients</Typography>}>
        <SearchBox value={search} onChange={setSearch} placeholder="Search clients…" />
      </FilterBar>

      <DataTable
        columns={columns}
        rows={filtered}
        loading={isLoading}
        actions={(c) => (
          <Box
            sx={{
              display: "inline-flex",
              gap: "6px",
              padding: "4px",
              borderRadius: "12px",
              background: "linear-gradient(145deg, #f9fafb, #ffffff)",
              border: "1px solid #E5E7EB",
            }}
          >
            <Tooltip title="Regenerate key" arrow>
              <IconButton
                size="small"
                onClick={() => setConfirmRegenId(c.id)}
                sx={{ ...btnBase, color: "#1D4ED8", "&:hover": { ...btnBase["&:hover"], backgroundColor: "#DBEAFE", boxShadow: GOLD_GLOW } }}
              >
                <RefreshRoundedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            {c.status === "active" && (
              <Tooltip title="Revoke" arrow>
                <IconButton
                  size="small"
                  onClick={() => revokeClient(c.id)}
                  sx={{ ...btnBase, color: "#EA580C", "&:hover": { ...btnBase["&:hover"], backgroundColor: "#FEF3C7", boxShadow: GOLD_GLOW } }}
                >
                  <BlockIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            <Tooltip title="Delete" arrow>
              <IconButton
                size="small"
                onClick={() => setConfirmDeleteId(c.id)}
                sx={{ ...btnBase, color: "#EF4444", "&:hover": { ...btnBase["&:hover"], backgroundColor: "#FEE2E2", boxShadow: GOLD_GLOW } }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        )}
        empty={{
          icon: <VpnKeyRoundedIcon />,
          title: "No API clients yet",
          message: "Create an API key to allow external systems to connect to INTALK.",
          action: (
            <ActionButton startIcon={<AddRoundedIcon />} onClick={() => { setName(""); setNewKey(null); setOpen(true); }}>
              Create API Key
            </ActionButton>
          ),
        }}
      />

      {/* Create / reveal-once key dialog — kept as Dialog to preserve the reveal-once modal UX */}
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: { borderRadius: "16px" } }}
      >
        <DialogTitle sx={{ fontWeight: 800, color: NAVY }}>
          {newKey ? "Key Generated" : "Create API Key"}
        </DialogTitle>
        <DialogContent>
          {!newKey ? (
            <TextField
              label="Client Name (e.g. DCC CRM)"
              value={name}
              onChange={(e) => setName(e.target.value)}
              fullWidth
              sx={{ mt: 1 }}
              required
            />
          ) : (
            <CardSurface noHover sx={{ p: 2, mt: 1 }}>
              <Alert severity="success" sx={{ mb: 1.5, borderRadius: "10px" }}>
                Copy this key now — it will not be shown again.
              </Alert>
              <Box
                component="code"
                sx={{ display: "block", wordBreak: "break-all", fontSize: 13, color: NAVY, userSelect: "all" }}
              >
                {newKey}
              </Box>
            </CardSurface>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
          <Button variant="outlined" onClick={() => setOpen(false)} sx={{ borderRadius: "10px" }}>
            {newKey ? "Done" : "Cancel"}
          </Button>
          {!newKey && (
            <ActionButton onClick={save} disabled={!name}>Create</ActionButton>
          )}
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={confirmRegenId !== null}
        title="Regenerate API key?"
        message="The old key stops working immediately. Make sure to update any services using it."
        confirmText="Regenerate"
        danger
        onClose={() => setConfirmRegenId(null)}
        onConfirm={handleRegenConfirm}
      />

      <ConfirmDialog
        open={confirmDeleteId !== null}
        title="Delete API client?"
        message="This permanently removes the client and its key. This cannot be undone."
        confirmText="Delete"
        danger
        onClose={() => setConfirmDeleteId(null)}
        onConfirm={async () => { if (confirmDeleteId !== null) await deleteClient(confirmDeleteId); setConfirmDeleteId(null); }}
      />
    </AppPage>
  );
};

export default ApiClients;
